import * as React from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface CoinGeckoCoin {
  id: string;
  symbol: string;
  name: string;
}

export interface CryptoSearchDropdownProps {
  onSelect: (coin: { symbol: string; name: string }) => void;
  disabled?: boolean;
}

const COINGECKO_COINS_LIST_URL = 'https://api.coingecko.com/api/v3/coins/list';
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 20;
const MIN_QUERY_LENGTH = 2;

export function CryptoSearchDropdown({ onSelect, disabled }: CryptoSearchDropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');
  const [coins, setCoins] = React.useState<CoinGeckoCoin[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [apiError, setApiError] = React.useState(false);
  const [selectedLabel, setSelectedLabel] = React.useState('');

  // Debounce the query
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // Fetch coins list once when dropdown opens (or on first search)
  const coinsRef = React.useRef<CoinGeckoCoin[]>([]);
  const fetchedRef = React.useRef(false);

  const fetchCoinsList = React.useCallback(async () => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    try {
      const res = await fetch(COINGECKO_COINS_LIST_URL);
      if (!res.ok) throw new Error('Failed to fetch coins list');
      const data: CoinGeckoCoin[] = await res.json();
      coinsRef.current = data;
      setApiError(false);
    } catch {
      setApiError(true);
      fetchedRef.current = false; // allow retry
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (open) {
      fetchCoinsList();
    }
  }, [open, fetchCoinsList]);

  // Filter results based on debounced query
  const results = React.useMemo(() => {
    if (debouncedQuery.length < MIN_QUERY_LENGTH) return [];
    const q = debouncedQuery.toLowerCase();
    return coinsRef.current
      .filter(
        (c) =>
          c.symbol.toLowerCase().includes(q) ||
          c.name.toLowerCase().includes(q)
      )
      .slice(0, MAX_RESULTS);
  }, [debouncedQuery]);

  const handleSelect = (coin: CoinGeckoCoin) => {
    const symbol = coin.symbol.toUpperCase();
    setSelectedLabel(`${symbol} — ${coin.name}`);
    setOpen(false);
    setQuery('');
    onSelect({ symbol, name: coin.name });
  };

  const showManualFallback = apiError;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('w-full justify-between', !selectedLabel && 'text-muted-foreground')}
          disabled={disabled}
        >
          {selectedLabel || 'Search cryptocurrency...'}
          {loading ? (
            <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={
              showManualFallback
                ? 'API unavailable — type symbol manually...'
                : 'Search by name or symbol...'
            }
            value={query}
            onValueChange={setQuery}
            data-testid="crypto-search-input"
          />
          <CommandList>
            {loading && (
              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading coins...
              </div>
            )}
            {!loading && showManualFallback && (
              <div className="px-3 py-2 text-sm text-muted-foreground">
                CoinGecko is unavailable. You can type the symbol and name manually in the fields
                below.
              </div>
            )}
            {!loading && !showManualFallback && debouncedQuery.length < MIN_QUERY_LENGTH && (
              <CommandEmpty>Type at least 2 characters to search.</CommandEmpty>
            )}
            {!loading && !showManualFallback && debouncedQuery.length >= MIN_QUERY_LENGTH && results.length === 0 && (
              <CommandEmpty>No results found.</CommandEmpty>
            )}
            {!loading && results.length > 0 && (
              <CommandGroup>
                {results.map((coin) => (
                  <CommandItem
                    key={coin.id}
                    value={coin.id}
                    onSelect={() => handleSelect(coin)}
                  >
                    <Check
                      className={cn(
                        'mr-2 h-4 w-4',
                        selectedLabel === `${coin.symbol.toUpperCase()} — ${coin.name}`
                          ? 'opacity-100'
                          : 'opacity-0'
                      )}
                    />
                    <span className="font-medium">{coin.symbol.toUpperCase()}</span>
                    <span className="ml-2 text-muted-foreground">{coin.name}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
